import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Scale, TrendingUp, ShieldCheck, FileWarning, Phone, Info, BookOpen } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import DismissalRiskAssessment from "../components/DismissalRiskAssessment";
import ContactSection from "../components/ContactSection";
import { useSEO, useJsonLd, SITE } from "../lib/seo";

const PATH = "/valutatore-rischio-licenziamento";
const H1 = "Valutatore rischio economico da licenziamento";
const META_TITLE = "Calcolo indennità licenziamento illegittimo — Jobs Act, Art. 18, Fornero";
const META_DESCRIPTION =
    "Strumento gratuito per calcolare l'indennità economica da licenziamento illegittimo secondo Jobs Act (D.Lgs. 23/2015), art. 18 St.Lav. e Legge Fornero. Stima dal punto di vista di lavoratore o azienda.";

const FAQS = [
    {
        q: "Quanto vale un licenziamento illegittimo secondo il Jobs Act?",
        a: "Per i lavoratori assunti dopo il 7 marzo 2015 in aziende con più di 15 dipendenti, il D.Lgs. 23/2015 (come modificato dalla Corte Costituzionale con sent. 194/2018 e dal D.L. 87/2018) prevede un'indennità risarcitoria da 6 a 36 mensilità dell'ultima retribuzione di riferimento per il calcolo del TFR. Il criterio non è più automatico (2 per anno di anzianità) ma valuta anzianità, dimensioni aziendali, comportamento delle parti e condizioni di attività.",
    },
    {
        q: "In quali casi scatta la reintegra nel posto di lavoro?",
        a: "La reintegra è obbligatoria in caso di licenziamento nullo perché discriminatorio, ritorsivo, intimato per motivo illecito determinante o comminato oralmente (art. 18 c.1 St.Lav. / art. 2 D.Lgs. 23/2015). È inoltre prevista, per il rito Fornero (ante 2015), in caso di manifesta insussistenza del fatto contestato disciplinare o della giustificazione del motivo oggettivo. Post-Jobs Act la reintegra 'attenuata' resta solo per il disciplinare quando il fatto materiale contestato è insussistente.",
    },
    {
        q: "Cos'è l'indennità sostitutiva della reintegra da 15 mensilità?",
        a: "Se un giudice ordina la reintegra, il lavoratore può scegliere — entro 30 giorni dalla comunicazione del deposito della sentenza — di NON tornare al lavoro e ricevere in alternativa un'indennità pari a 15 mensilità dell'ultima retribuzione globale di fatto. Tale importo si somma al risarcimento del periodo tra licenziamento e sentenza (minimo 5 mensilità in caso di reintegra ex art. 18) ed è normalmente esente da imposte fino a determinati limiti.",
    },
    {
        q: "Quanto costa a un'azienda un licenziamento contestato?",
        a: "Oltre al rischio risarcitorio (6-36 mensilità in regime Jobs Act, 12-24 con art. 18), l'azienda paga: (1) il 'ticket licenziamento' INPS pari al 41% del massimale NASpI per ogni anno di anzianità (max 3 anni), tipicamente ~€1.800-€2.500; (2) le spese legali del doppio grado di giudizio, calcolate secondo il D.M. 55/2014 in base al valore della causa (~€3.500-€8.000 per scaglione medio); (3) eventuali rivalutazioni monetarie e interessi. Una transazione al 40-60% del rischio massimo evita l'incertezza e i costi accessori.",
    },
    {
        q: "Cosa cambia tra Jobs Act e vecchio Art. 18 (Fornero)?",
        a: "Il Jobs Act (D.Lgs. 23/2015) si applica a chi è stato assunto dopo il 7 marzo 2015 e prevede indennità 6-36 mensilità (post Corte Cost. 194/2018 il criterio è discrezionale). L'art. 18 St.Lav. riformato dalla L. 92/2012 (Fornero) si applica agli assunti prima di quella data: prevede tutela reale attenuata (reintegra + max 12 mensilità) o indennitaria forte (12-24 mensilità) a seconda della gravità del vizio. Nelle piccole imprese (fino a 15 dipendenti) si applica la L. 108/1990: indennità 2,5-6 mensilità senza reintegra, salvo casi di nullità.",
    },
    {
        q: "Come funziona la buonuscita in incentivo all'esodo?",
        a: "L'incentivo all'esodo è una somma pagata dal datore per convincere il lavoratore a risolvere consensualmente il rapporto, evitando il licenziamento e il contenzioso. Tipicamente si negozia in una fascia del 50-75% del rischio massimo teorico che l'azienda subirebbe in tribunale. Se stipulato in sede protetta (Ispettorato del Lavoro, sindacale, avvocati) è esente da imposte fino a 1 mensilità per ogni anno di servizio, oltre a essere non impugnabile.",
    },
    {
        q: "Il licenziamento verbale è sempre nullo?",
        a: "Sì. L'art. 2 c.1 L. 604/1966 impone la forma scritta a pena di nullità. Un licenziamento comunicato oralmente (anche via telefono, messaggio non firmato, o solo con fatti concludenti come blocco degli accessi aziendali) è nullo: si applica la tutela reale piena — reintegra obbligatoria + risarcimento minimo 5 mensilità fino alla data della sentenza, senza tetto massimo. È tra le tutele più forti previste dall'ordinamento.",
    },
    {
        q: "Lo strumento fornisce un valore giuridicamente vincolante?",
        a: "No. Il valutatore è uno strumento divulgativo che restituisce stime orientative basate sui parametri normativi standard. Ogni caso concreto richiede la valutazione di elementi specifici — clausole contrattuali, comportamento delle parti, prove documentali, vizi procedurali — che possono modificare significativamente sia in aumento sia in riduzione la valutazione. Per un parere legale vincolante è necessaria un'analisi personalizzata da parte di un avvocato giuslavorista.",
    },
];

export default function RiskAssessmentPage() {
    useSEO({
        title: META_TITLE,
        description: META_DESCRIPTION,
        path: PATH,
    });

    useJsonLd(
        [
            {
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                    { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE.url}/` },
                    { "@type": "ListItem", "position": 2, "name": H1, "item": `${SITE.url}${PATH}` },
                ],
            },
            {
                "@context": "https://schema.org",
                "@type": "WebApplication",
                "name": "Valutatore rischio economico da licenziamento",
                "url": `${SITE.url}${PATH}`,
                "applicationCategory": "LegalService",
                "operatingSystem": "Web",
                "offers": { "@type": "Offer", "price": "0", "priceCurrency": "EUR" },
                "description": META_DESCRIPTION,
                "inLanguage": "it-IT",
                "publisher": { "@id": `${SITE.url}/#legalservice` },
            },
            {
                "@context": "https://schema.org",
                "@type": "HowTo",
                "name": "Come stimare il valore di un licenziamento illegittimo",
                "description":
                    "Guida per stimare in via orientativa l'esposizione economica di un licenziamento contestato in base a Jobs Act, art. 18 Statuto dei Lavoratori e Legge Fornero.",
                "step": [
                    {
                        "@type": "HowToStep",
                        "position": 1,
                        "name": "Identifica il regime giuridico applicabile",
                        "text":
                            "Verifica la data di assunzione: se successiva al 7 marzo 2015 si applica il Jobs Act (D.Lgs. 23/2015), altrimenti l'art. 18 St.Lav. riformato dalla Legge Fornero (L. 92/2012).",
                    },
                    {
                        "@type": "HowToStep",
                        "position": 2,
                        "name": "Verifica la dimensione dell'azienda",
                        "text":
                            "Le aziende con più di 15 dipendenti (nell'unità produttiva o nel Comune, con specifici criteri di computo) rientrano nella tutela forte. Fino a 15 dipendenti si applica la L. 108/1990 con indennità ridotta.",
                    },
                    {
                        "@type": "HowToStep",
                        "position": 3,
                        "name": "Calcola l'indennità in base ad anzianità e retribuzione",
                        "text":
                            "Moltiplica le mensilità previste dal regime applicabile per l'ultima retribuzione utile al TFR (per Jobs Act) o retribuzione globale di fatto (per art. 18). L'anzianità determina il numero di mensilità entro i range di legge.",
                    },
                    {
                        "@type": "HowToStep",
                        "position": 4,
                        "name": "Valuta i profili di nullità o reintegra",
                        "text":
                            "Il licenziamento discriminatorio, ritorsivo o verbale comporta sempre la reintegra obbligatoria + risarcimento. In caso di reintegra il lavoratore può scegliere l'indennità sostitutiva di 15 mensilità entro 30 giorni dalla sentenza.",
                    },
                    {
                        "@type": "HowToStep",
                        "position": 5,
                        "name": "Considera una soluzione transattiva",
                        "text":
                            "Una transazione stragiudiziale in sede protetta (ITL, sindacale) chiude tipicamente al 40-75% del rischio massimo teorico ed evita costi accessori, incertezza e tempi lunghi del giudizio.",
                    },
                ],
            },
            {
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": FAQS.map((f) => ({
                    "@type": "Question",
                    "name": f.q,
                    "acceptedAnswer": { "@type": "Answer", "text": f.a },
                })),
            },
        ],
        "risk-assessment-jsonld",
    );

    return (
        <div data-testid="risk-assessment-page">
            <Header />
            <main>
                {/* Hero */}
                <section className="relative pt-40 pb-16 lg:pt-48 lg:pb-24 bg-navy overflow-hidden">
                    <div className="absolute top-0 bottom-0 left-10 lg:left-24 w-px bg-gold/25 hidden md:block" />
                    <div className="max-w-5xl mx-auto px-6 lg:px-10 relative z-10">
                        <div className="flex items-center gap-4 mb-6">
                            <span className="block w-16 h-[2px] bg-gold" />
                            <span className="label-eyebrow !text-gold">Strumento gratuito</span>
                        </div>
                        <h1 className="font-serif text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-6 max-w-4xl">
                            Valutatore rischio economico da <span className="italic text-gold">licenziamento</span>
                        </h1>
                        <p className="text-slate-300 text-base md:text-lg leading-relaxed max-w-3xl">
                            Calcola in pochi secondi l&apos;esposizione economica di un licenziamento contestato secondo
                            Jobs Act, art. 18 Statuto dei Lavoratori e Legge Fornero — dal punto di vista dell&apos;azienda
                            (rischio) o del lavoratore (opportunità).
                        </p>
                    </div>
                </section>

                {/* Tool */}
                <DismissalRiskAssessment />

                {/* Normativa & approfondimento */}
                <section className="py-24 lg:py-32 bg-white">
                    <div className="max-w-5xl mx-auto px-6 lg:px-10">
                        <div className="flex items-center gap-4 mb-6">
                            <span className="gold-rule" />
                            <span className="label-eyebrow">Quadro normativo</span>
                        </div>
                        <h2 className="font-serif text-navy text-3xl md:text-4xl leading-[1.15] mb-12 max-w-3xl">
                            I tre regimi di tutela contro il licenziamento illegittimo
                        </h2>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-16">
                            <div className="border-l-4 border-gold pl-6">
                                <div className="flex items-center gap-3 mb-3">
                                    <ShieldCheck size={20} className="text-gold-deep" />
                                    <h3 className="font-serif text-navy text-xl">Tutela reale piena</h3>
                                </div>
                                <p className="text-slate-700 leading-relaxed text-sm">
                                    Si applica ai licenziamenti <strong>nulli</strong> (discriminatori, ritorsivi, orali,
                                    per motivo illecito). Reintegra obbligatoria + risarcimento minimo 5 mensilità fino
                                    a sentenza + possibilità di optare per 15 mensilità sostitutive. Nessun tetto massimo.
                                </p>
                            </div>
                            <div className="border-l-4 border-gold pl-6">
                                <div className="flex items-center gap-3 mb-3">
                                    <Scale size={20} className="text-gold-deep" />
                                    <h3 className="font-serif text-navy text-xl">Tutela reale attenuata</h3>
                                </div>
                                <p className="text-slate-700 leading-relaxed text-sm">
                                    Per assunti <strong>ante 7 marzo 2015</strong> in aziende &gt; 15 dipendenti
                                    (art. 18 St.Lav. Fornero). Reintegra + max 12 mensilità in caso di manifesta
                                    insussistenza del fatto, altrimenti indennità 12-24 mensilità.
                                </p>
                            </div>
                            <div className="border-l-4 border-gold pl-6">
                                <div className="flex items-center gap-3 mb-3">
                                    <TrendingUp size={20} className="text-gold-deep" />
                                    <h3 className="font-serif text-navy text-xl">Tutela indennitaria</h3>
                                </div>
                                <p className="text-slate-700 leading-relaxed text-sm">
                                    Regime <strong>Jobs Act</strong> (post 2015, &gt; 15 dip.): indennità 6-36 mensilità
                                    determinata dal giudice caso per caso (Corte Cost. 194/2018). Piccole imprese
                                    (≤ 15 dip.): indennità dimezzata 3-6 mensilità.
                                </p>
                            </div>
                        </div>

                        <div className="bg-slate-50 border border-slate-200 p-8 lg:p-10">
                            <div className="flex items-start gap-4">
                                <BookOpen size={24} className="text-gold-deep flex-shrink-0 mt-1" />
                                <div>
                                    <h3 className="font-serif text-navy text-2xl mb-3">Riferimenti normativi principali</h3>
                                    <ul className="space-y-2 text-slate-700 leading-relaxed text-sm">
                                        <li className="flex items-start gap-2">
                                            <Check size={16} className="text-gold-deep mt-1 flex-shrink-0" />
                                            <span><strong>L. 604/1966</strong> — disciplina generale dei licenziamenti individuali (giusta causa, giustificato motivo, forma scritta, termini di impugnazione)</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <Check size={16} className="text-gold-deep mt-1 flex-shrink-0" />
                                            <span><strong>Art. 18 L. 300/1970 (Statuto dei Lavoratori)</strong> come riformato dalla <strong>L. 92/2012 Fornero</strong> — tutele per assunti ante 2015</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <Check size={16} className="text-gold-deep mt-1 flex-shrink-0" />
                                            <span><strong>D.Lgs. 23/2015 (Jobs Act — Tutele Crescenti)</strong> — regime per assunti dopo il 7 marzo 2015, come mod. da Corte Cost. 194/2018 e D.L. 87/2018</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <Check size={16} className="text-gold-deep mt-1 flex-shrink-0" />
                                            <span><strong>L. 108/1990</strong> — regime della tutela obbligatoria per aziende fino a 15 dipendenti</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <Check size={16} className="text-gold-deep mt-1 flex-shrink-0" />
                                            <span><strong>D.M. 55/2014</strong> — parametri forensi per il calcolo delle spese legali</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <Check size={16} className="text-gold-deep mt-1 flex-shrink-0" />
                                            <span><strong>Art. 2 c. 31-33 L. 92/2012</strong> — disciplina del ticket licenziamento INPS</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Cross-link */}
                        <div className="mt-10 flex items-center gap-4 flex-wrap">
                            <span className="label-eyebrow">Vedi anche:</span>
                            <Link
                                to="/calcolo-termini-impugnazione-licenziamento"
                                className="inline-flex items-center gap-2 text-navy hover:text-gold-deep transition-colors text-sm font-semibold border-b border-gold pb-0.5"
                            >
                                Calcolo termini per impugnare il licenziamento
                                <ArrowRight size={14} />
                            </Link>
                            <Link
                                to="/guide/avvocato-licenziamento-illegittimo-piacenza"
                                className="inline-flex items-center gap-2 text-navy hover:text-gold-deep transition-colors text-sm font-semibold border-b border-gold pb-0.5"
                            >
                                Guida al licenziamento illegittimo
                                <ArrowRight size={14} />
                            </Link>
                        </div>
                    </div>
                </section>

                {/* FAQ */}
                <section className="py-24 lg:py-32 bg-[#FAFAFA]">
                    <div className="max-w-5xl mx-auto px-6 lg:px-10">
                        <div className="flex items-center gap-4 mb-6">
                            <span className="gold-rule" />
                            <span className="label-eyebrow">Domande frequenti</span>
                        </div>
                        <h2 className="font-serif text-navy text-3xl md:text-4xl leading-[1.15] mb-12 max-w-3xl">
                            FAQ sul valore economico del licenziamento
                        </h2>
                        <div className="divide-y divide-slate-200 border-y border-slate-200">
                            {FAQS.map((f, i) => (
                                <details key={i} className="group py-6" data-testid={`risk-faq-${i}`}>
                                    <summary className="flex items-start justify-between gap-6 cursor-pointer list-none">
                                        <h3 className="font-serif text-navy text-xl md:text-2xl leading-tight group-hover:text-gold-deep transition-colors">
                                            {f.q}
                                        </h3>
                                        <span className="flex-shrink-0 w-10 h-10 border border-navy/30 group-hover:border-gold group-hover:bg-gold flex items-center justify-center transition-all font-serif text-xl text-navy group-open:rotate-45">
                                            +
                                        </span>
                                    </summary>
                                    <p className="mt-4 text-slate-600 text-base md:text-lg leading-relaxed pr-16">
                                        {f.a}
                                    </p>
                                </details>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CTA */}
                <section className="py-20 bg-navy">
                    <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
                        <FileWarning size={32} className="text-gold mx-auto mb-6" />
                        <h2 className="font-serif text-white text-3xl md:text-4xl leading-tight mb-6">
                            Vuoi una valutazione precisa e personalizzata?
                        </h2>
                        <p className="text-slate-300 text-base md:text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
                            Ogni caso ha specificità che il valutatore automatico non può cogliere. La prima consulenza
                            è sempre <strong className="text-white">gratuita</strong>.
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <Link
                                to="/#contatti"
                                data-testid="risk-page-cta"
                                className="btn-gold group"
                            >
                                Richiedi consulenza
                                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                            </Link>
                            <a
                                href="tel:0523468737"
                                className="inline-flex items-center gap-2 px-7 py-3 border border-white/30 text-white font-semibold tracking-wide text-sm transition-all duration-300 hover:border-gold hover:text-gold"
                            >
                                <Phone size={16} /> 0523 468737
                            </a>
                        </div>
                    </div>
                </section>

                <ContactSection />
            </main>
            <Footer />
        </div>
    );
}
